import React from "react";

/**
 * lowercase + NFD + strip combining marks → case & accent insensitive (κρίσιμο για ελληνικά).
 * Επίσης ενοποιεί το τελικό σίγμα «ς» με το «σ» ώστε να μην παίζει ρόλο η θέση του στη λέξη.
 */
export function normalizeSearch(s: string | number | null | undefined): string {
  return (s ?? "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\u03c2/g, "\u03c3") // ς → σ
    .trim();
}

/** Split query into tokens (AND logic — κάθε token πρέπει να εμφανίζεται κάπου). */
export function tokenizeSearch(q: string): string[] {
  return normalizeSearch(q).split(/\s+/).filter(Boolean);
}

export function matchesTokens(haystack: string, tokens: string[]): boolean {
  if (tokens.length === 0) return true;
  const h = normalizeSearch(haystack);
  return tokens.every((t) => h.includes(t));
}

/** Fields we search across, joined into a single haystack string. */
export type SearchableProduct = {
  name?: string | null;
  ps_id?: number | null;
  reference?: string | null;
  ean13?: string | null;
  upc?: string | null;
  barcodes?: string[] | null;
  manufacturer_name?: string | null;
  category_name?: string | null;
  description_short?: string | null;
  manufacturer_id?: number | null;
  stock_quantity?: number | null;
  units_sold?: number | null;
};

export function productHaystack(p: SearchableProduct): string {
  return [
    p.name,
    p.ps_id,
    p.reference,
    p.ean13,
    p.upc,
    ...(p.barcodes ?? []),
    p.manufacturer_name,
    p.category_name,
    p.description_short,
  ]
    .filter((v) => v != null && v !== "")
    .join(" ");
}

/** Relevance score — higher is more relevant. `nq` is the normalized full query. */
export function scoreProduct(p: SearchableProduct, tokens: string[], nq: string): number {
  if (!nq) return 0;
  let score = 0;
  const name = normalizeSearch(p.name);
  const codes = [p.ean13, p.upc, p.reference, ...(p.barcodes ?? [])]
    .filter(Boolean)
    .map((c) => normalizeSearch(c));

  // Exact barcode / SKU match
  if (codes.some((c) => c === nq)) score += 200;

  // Name matches
  if (name === nq) score += 100;
  else if (name.startsWith(nq)) score += 30;
  else if (name.includes(nq)) score += 10;

  // Other field matches
  if (p.reference && normalizeSearch(p.reference).includes(nq)) score += 4;
  if (p.manufacturer_name && normalizeSearch(p.manufacturer_name).includes(nq)) score += 2;
  if (p.category_name && normalizeSearch(p.category_name).includes(nq)) score += 1;
  if (p.description_short && normalizeSearch(p.description_short).includes(nq)) score += 1;

  // Per-token coverage across name + brand + category (κρατάει σχετικά τα multi-word, ανεξάρτητα από τη σειρά)
  const brand = normalizeSearch(p.manufacturer_name);
  const cat = normalizeSearch(p.category_name);
  for (const t of tokens) {
    if (name.includes(t)) score += 2;
    else if (brand.includes(t) || cat.includes(t)) score += 1;
  }

  // In-stock bonus
  if ((p.stock_quantity ?? 0) > 0) score += 1;

  return score;
}

/** Wrap matched substrings in <mark>, keeping original characters (and accents). */
export function highlightText(text: string | null | undefined, tokens: string[]): React.ReactNode {
  const s = text ?? "";
  if (!tokens.length || !s) return s;
  const normTokens = tokens.map((t) => normalizeSearch(t)).filter(Boolean);
  if (!normTokens.length) return s;

  let norm = "";
  const map: number[] = [];
  for (let i = 0; i < s.length; i++) {
    const n = s[i]
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\u03c2/g, "\u03c3");
    for (let j = 0; j < n.length; j++) map.push(i);
    norm += n;
  }

  const ranges: Array<[number, number]> = [];
  for (const t of normTokens) {
    if (!t) continue;
    let idx = 0;
    while ((idx = norm.indexOf(t, idx)) !== -1) {
      const origStart = map[idx] ?? 0;
      const origEnd = (map[idx + t.length - 1] ?? s.length - 1) + 1;
      ranges.push([origStart, origEnd]);
      idx += t.length;
    }
  }
  if (!ranges.length) return s;

  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  }

  const out: React.ReactNode[] = [];
  let cur = 0;
  merged.forEach(([a, b], i) => {
    if (cur < a) out.push(<span key={`s${i}`}>{s.slice(cur, a)}</span>);
    out.push(
      <mark key={`m${i}`} className="rounded-[2px] bg-primary/20 px-0.5 text-foreground">
        {s.slice(a, b)}
      </mark>,
    );
    cur = b;
  });
  if (cur < s.length) out.push(<span key="end">{s.slice(cur)}</span>);
  return out;
}
